import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Alert,
  Chip,
  InputAdornment,
} from '@mui/material';
import { Product } from '../types';
import {
  getMaxCustomCaseQuantity,
  isValidCustomQuantity,
  calculateCustomCasePrice,
  formatCasePrice,
  CUSTOM_CASE_FEE_CENTS,
} from '../utils/caseQuantity';

interface CustomCaseInputProps {
  product: Product;
  value: number;
  onChange: (value: number) => void;
  size?: 'small' | 'medium';
}

const CustomCaseInput: React.FC<CustomCaseInputProps> = ({
  product,
  value,
  onChange,
  size = 'medium',
}) => {
  const [inputValue, setInputValue] = useState<string>(value.toString());
  const [error, setError] = useState<string>('');
  
  const maxQuantity = getMaxCustomCaseQuantity(product);
  const caseQuantities = product.case_quantities || [];

  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  const validateAndUpdate = (newValue: string) => {
    setInputValue(newValue);
    
    // Clear error initially
    setError('');
    
    // Allow empty input (will be handled on blur)
    if (newValue === '') {
      return;
    }
    
    // Check if it's a valid number
    const numValue = parseInt(newValue, 10);
    if (isNaN(numValue)) {
      setError('Please enter a valid number');
      return;
    }
    
    // Validate the quantity
    if (numValue <= 0) {
      setError('Quantity must be greater than 0');
      return;
    }
    
    if (numValue > maxQuantity) {
      setError(`Custom case quantity cannot exceed ${maxQuantity}`);
      return;
    }
    
    if (caseQuantities.includes(numValue)) {
      setError(`${numValue} is a standard case size. Use standard case instead.`);
      return;
    }
    
    // Valid custom quantity
    onChange(numValue);
  };

  const handleBlur = () => {
    if (inputValue === '' || isNaN(parseInt(inputValue, 10))) {
      // Reset to last valid value or default
      const defaultValue = Math.min(1, maxQuantity);
      setInputValue(defaultValue.toString());
      onChange(defaultValue);
      setError('');
    }
  };

  const totalPrice = calculateCustomCasePrice(product, parseInt(inputValue, 10) || 0);

  return (
    <Box>
      <TextField
        fullWidth
        type="number"
        size={size}
        label="Custom Case Quantity"
        value={inputValue}
        onChange={(e) => validateAndUpdate(e.target.value)}
        onBlur={handleBlur}
        error={!!error}
        helperText={error || `Max: ${maxQuantity} units`}
        InputProps={{
          inputProps: { min: 1, max: maxQuantity },
          endAdornment: (
            <InputAdornment position="end">
              <Typography variant="caption" color="text.secondary">
                units
              </Typography>
            </InputAdornment>
          ),
        }}
      />
      
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`Custom Packing Fee: ${formatCasePrice(CUSTOM_CASE_FEE_CENTS)}`}
            color="warning"
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            +
          </Typography>
          <Chip
            label={`Base Price: ${formatCasePrice(totalPrice - CUSTOM_CASE_FEE_CENTS)}`}
            color="default"
            size="small"
            variant="outlined"
          />
        </Box>
        
        {!error && inputValue && !isNaN(parseInt(inputValue, 10)) && (
          <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Total Case Price: {formatCasePrice(totalPrice)}
          </Typography>
        )}
      </Box>
      
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Custom cases</strong> let you order any quantity up to {maxQuantity} units with a {formatCasePrice(CUSTOM_CASE_FEE_CENTS)} custom packing fee.
          {caseQuantities.length > 0 && (
            <> Standard cases ({caseQuantities.join(', ')}) have no custom packing fee.</>
          )}
        </Typography>
      </Alert>
    </Box>
  );
};

export default CustomCaseInput;

